import React, { useState, useEffect } from 'react';

interface PLData {
  by_year: {
    [year: string]: {
      revenue: number;
      cogs: number;
      payroll: number;
      expenses: number;
    };
  };
  totals: {
    total_revenue: number;
    total_cogs: number;
    total_payroll: number;
    total_expenses: number;
  };
}

interface EditablePLTableProps {
  data: PLData;
  assumptions: any[];
  onChange: (updatedPL: PLData, userOverrides: any) => void;
}

interface EditingCell {
  year: string;
  field: string;
}

export default function EditablePLTable({ data, assumptions, onChange }: EditablePLTableProps) {
  const [plData, setPLData] = useState<PLData>(data);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [userOverrides, setUserOverrides] = useState<Record<string, Record<string, number>>>({});
  const [showCOGSHelper, setShowCOGSHelper] = useState(false);
  const [cogsPercentage, setCOGSPercentage] = useState(35);

  // Sync with parent data
  useEffect(() => {
    setPLData(data);
  }, [data]);

  const years = Object.keys(plData.by_year || {}).sort();

  const handleCellClick = (year: string, field: string, currentValue: number) => {
    setEditingCell({ year, field });
    setEditValue(currentValue.toString());
  };

  const handleCellBlur = () => {
    if (!editingCell) return;

    const newValue = parseFloat(editValue) || 0;
    const { year, field } = editingCell;

    // Update the data
    const updatedPL = { ...plData };
    updatedPL.by_year = { ...updatedPL.by_year };
    updatedPL.by_year[year] = { ...updatedPL.by_year[year], [field]: newValue };

    // Recalculate totals
    updatedPL.totals = {
      total_revenue: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.revenue || 0), 0),
      total_cogs: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.cogs || 0), 0),
      total_payroll: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.payroll || 0), 0),
      total_expenses: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.expenses || 0), 0),
    };

    // Track user override
    const newOverrides = { ...userOverrides };
    if (!newOverrides[year]) newOverrides[year] = {};
    newOverrides[year][field] = newValue;
    setUserOverrides(newOverrides);

    setPLData(updatedPL);
    onChange(updatedPL, newOverrides);
    setEditingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const applyCOGSPercentage = () => {
    const updatedPL = { ...plData };
    updatedPL.by_year = { ...updatedPL.by_year };
    
    const newOverrides = { ...userOverrides };

    years.forEach(year => {
      const revenue = updatedPL.by_year[year].revenue || 0;
      const newCOGS = Math.round(revenue * (cogsPercentage / 100));
      updatedPL.by_year[year] = { ...updatedPL.by_year[year], cogs: newCOGS };
      
      if (!newOverrides[year]) newOverrides[year] = {};
      newOverrides[year].cogs = newCOGS;
    });

    // Recalculate totals
    updatedPL.totals = {
      ...updatedPL.totals,
      total_cogs: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.cogs || 0), 0),
    };

    setPLData(updatedPL);
    setUserOverrides(newOverrides);
    onChange(updatedPL, newOverrides);
    setShowCOGSHelper(false);
  };

  const resetToEstimated = () => {
    setPLData(data);
    setUserOverrides({});
    onChange(data, {});
  };

  const isOverridden = (year: string, field: string) => {
    return userOverrides[year]?.[field] !== undefined;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const renderEditableCell = (year: string, field: string, value: number, bgClass: string = '') => {
    const isEditing = editingCell?.year === year && editingCell?.field === field;
    const wasOverridden = isOverridden(year, field);

    if (isEditing) {
      return (
        <td key={`${year}-${field}`} className={`px-2 py-1 ${bgClass}`}>
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full px-2 py-1 text-right border-2 border-blue-500 rounded focus:outline-none text-sm"
          />
        </td>
      );
    }

    return (
      <td
        key={`${year}-${field}`}
        onClick={() => handleCellClick(year, field, value)}
        className={`text-right px-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${bgClass} ${
          wasOverridden ? 'ring-2 ring-blue-300 ring-inset' : ''
        }`}
        title={wasOverridden ? 'User override (click to edit)' : 'Click to edit'}
      >
        <span className="flex items-center justify-end gap-1">
          {wasOverridden && <span className="text-blue-600 text-xs">✎</span>}
          {formatCurrency(value)}
        </span>
      </td>
    );
  };

  const grossProfit = (plData.totals?.total_revenue || 0) - (plData.totals?.total_cogs || 0);
  const hasOverrides = Object.keys(userOverrides).length > 0;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">💡 Click any value to edit</span>
          {hasOverrides && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
              {Object.values(userOverrides).reduce((count, year) => count + Object.keys(year).length, 0)} overrides
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCOGSHelper(!showCOGSHelper)}
            className="px-3 py-1 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors"
          >
            ⚡ Adjust COGS %
          </button>
          {hasOverrides && (
            <button
              onClick={resetToEstimated}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              ↺ Reset All
            </button>
          )}
        </div>
      </div>

      {/* COGS Helper */}
      {showCOGSHelper && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="font-medium text-amber-900 mb-2">Adjust COGS as % of Revenue</h4>
          <p className="text-sm text-amber-700 mb-3">
            Apply a uniform COGS percentage across all years. Industry defaults:
            <span className="block mt-1">
              • Restaurants: 28-35% • Retail: 40-60% • Services: 15-25%
            </span>
          </p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="10"
              max="70"
              value={cogsPercentage}
              onChange={(e) => setCOGSPercentage(Number(e.target.value))}
              className="flex-1"
            />
            <input
              type="number"
              value={cogsPercentage}
              onChange={(e) => setCOGSPercentage(Number(e.target.value))}
              className="w-16 px-2 py-1 border rounded text-center"
            />
            <span className="text-amber-800">%</span>
            <button
              onClick={applyCOGSPercentage}
              className="px-4 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-700 w-40">Category</th>
              {years.map(year => (
                <th key={year} className="text-right px-4 py-3 font-semibold text-gray-700 min-w-[120px]">
                  {year}
                </th>
              ))}
              <th className="text-right px-4 py-3 font-semibold text-gray-700 min-w-[120px] bg-gray-200">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Revenue */}
            <tr className="bg-green-50 hover:bg-green-100">
              <td className="px-4 py-2 font-medium text-green-800">
                Revenue
                <span className="block text-xs font-normal text-green-600">From deposits</span>
              </td>
              {years.map(year => renderEditableCell(year, 'revenue', plData.by_year[year]?.revenue || 0, 'text-green-700'))}
              <td className="text-right px-4 py-2 font-bold text-green-800 bg-green-100">
                {formatCurrency(plData.totals?.total_revenue || 0)}
              </td>
            </tr>

            {/* COGS */}
            <tr className="bg-amber-50 hover:bg-amber-100">
              <td className="px-4 py-2 font-medium text-amber-800">
                COGS
                <span className="block text-xs font-normal text-amber-600">
                  {assumptions?.find(a => a.field === 'cogs') ? '⚡ Estimated' : 'Direct'}
                </span>
              </td>
              {years.map(year => renderEditableCell(year, 'cogs', plData.by_year[year]?.cogs || 0, 'text-amber-700'))}
              <td className="text-right px-4 py-2 font-bold text-amber-800 bg-amber-100">
                {formatCurrency(plData.totals?.total_cogs || 0)}
              </td>
            </tr>

            {/* Payroll */}
            <tr className="hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-800">
                Payroll
                <span className="block text-xs font-normal text-gray-500">Labor costs</span>
              </td>
              {years.map(year => renderEditableCell(year, 'payroll', plData.by_year[year]?.payroll || 0))}
              <td className="text-right px-4 py-2 font-bold text-gray-800 bg-gray-100">
                {formatCurrency(plData.totals?.total_payroll || 0)}
              </td>
            </tr>

            {/* Other Expenses */}
            <tr className="hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-800">
                Other Expenses
                <span className="block text-xs font-normal text-gray-500">Rent, utilities, etc.</span>
              </td>
              {years.map(year => renderEditableCell(year, 'expenses', plData.by_year[year]?.expenses || 0))}
              <td className="text-right px-4 py-2 font-bold text-gray-800 bg-gray-100">
                {formatCurrency(plData.totals?.total_expenses || 0)}
              </td>
            </tr>

            {/* Gross Profit (calculated) */}
            <tr className="bg-blue-100 font-bold">
              <td className="px-4 py-3 text-blue-800">
                Gross Profit
                <span className="block text-xs font-normal">Revenue - COGS</span>
              </td>
              {years.map(year => {
                const yearGP = (plData.by_year[year]?.revenue || 0) - (plData.by_year[year]?.cogs || 0);
                return (
                  <td key={year} className="text-right px-4 py-3 text-blue-700">
                    {formatCurrency(yearGP)}
                  </td>
                );
              })}
              <td className="text-right px-4 py-3 text-blue-800 bg-blue-200">
                {formatCurrency(grossProfit)}
              </td>
            </tr>

            {/* Net Operating Income (calculated) */}
            <tr className="bg-blue-100 font-bold">
              <td className="px-4 py-3 text-blue-800">
                Net Operating Income
                <span className="block text-xs font-normal">GP - Payroll - Expenses</span>
              </td>
              {years.map(year => {
                const yearNOI = (plData.by_year[year]?.revenue || 0) 
                  - (plData.by_year[year]?.cogs || 0)
                  - (plData.by_year[year]?.payroll || 0)
                  - (plData.by_year[year]?.expenses || 0);
                return (
                  <td key={year} className="text-right px-4 py-3 text-blue-700">
                    {formatCurrency(yearNOI)}
                  </td>
                );
              })}
              <td className="text-right px-4 py-3 text-blue-800 bg-blue-200">
                {formatCurrency(
                  (plData.totals?.total_revenue || 0) 
                  - (plData.totals?.total_cogs || 0)
                  - (plData.totals?.total_payroll || 0)
                  - (plData.totals?.total_expenses || 0)
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Override Summary */}
      {hasOverrides && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <h4 className="font-medium text-blue-900 mb-2">📝 Your Overrides</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(userOverrides).map(([year, fields]) =>
              Object.entries(fields).map(([field, value]) => (
                <span
                  key={`${year}-${field}`}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                >
                  {year} {field}: {formatCurrency(value as number)}
                </span>
              ))
            )}
          </div>
          <p className="text-xs text-blue-600 mt-2">
            These overrides will be tracked in the audit trail for court defensibility.
          </p>
        </div>
      )}
    </div>
  );
}
